import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nContext';
import './chatbot.css';
import { ReactComponent as InfinitySymbol } from '../assets/assistant-infinity.svg';
import { getContactEmail } from '../common/contactEmail';

const endpoint = '/api/chat.php';
const email = getContactEmail();
const requestId = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');

function Avatar() {
  return <span className="chat-avatar" aria-hidden="true"><InfinitySymbol focusable="false" /></span>;
}

function EmailDraft({ draft, c }) {
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [notice, setNotice] = useState('');
  const mailto = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(`${c.recipient}: ${email}\n${c.subject}: ${subject}\n\n${body}`);
      setNotice(c.copied);
    } catch { setNotice(c.copyError); }
  }
  return <div className="chat-draft">
    <h3>{c.draft}</h3><p>{c.recipient} : <a href={`mailto:${email}`}>{email}</a></p>
    <label>{c.subject}<input value={subject} maxLength={160} onChange={e => setSubject(e.target.value)} /></label>
    <label>{c.body}<textarea value={body} maxLength={6000} rows={7} onChange={e => setBody(e.target.value)} /></label>
    <p className="chat-small">{c.mailHint}</p>
    <div className="chat-actions">
      {mailto.length <= 2000 && <a className="chat-button motion-button" href={mailto}>{c.openMail}</a>}
      <button className="chat-link motion-inline-link" type="button" onClick={copy}>{c.copy}</button>
    </div>
    {mailto.length > 2000 && <p className="chat-small">{c.longMail}</p>}
    <p className="chat-small" role="status">{notice}</p>
  </div>;
}

export default function Chatbot() {
  const { locale, t, pathFor, alternatePathFor } = useI18n();
  const c = t('chat');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState('connecting');
  const [error, setError] = useState(null);
  const [failed, setFailed] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const input = useRef(null), log = useRef(null), follow = useRef(true);
  const busy = useRef(false), active = useRef(null), mounted = useRef(false);
  const restoreFocus = useRef(false);
  const pending = phase === 'sending' || phase === 'connecting';
  const lastAssistantIndex = messages.reduce((last, message, index) => message.role === 'assistant' ? index : last, -1);

  async function api(payload, signal, csrf = session?.csrf || '') {
    const response = await fetch(endpoint, {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal,
      headers: payload ? { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf } : {},
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    let data;
    try { data = await response.json(); } catch { throw new Error('unavailable'); }
    if (!response.ok) { const failure = new Error(data.error || 'unavailable'); failure.retryNew = data.retryNew === true; throw failure; }
    return data;
  }

  async function connect() {
    if (busy.current) return;
    busy.current = true; setPhase('connecting'); setError(null);
    const controller = new AbortController(); active.current = controller;
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const data = await api(null, controller.signal);
      if (!mounted.current) return;
      setSession(data); setMessages(data.messages || []); setFailed(null); setPhase('ready');
    } catch (e) {
      if (mounted.current) { setError(e.name === 'AbortError' ? 'timeout' : e.message); setPhase('error'); }
    } finally { clearTimeout(timer); busy.current = false; }
  }

  useEffect(() => {
    mounted.current = true;
    const start = setTimeout(connect, 0);
    return () => { clearTimeout(start); mounted.current = false; active.current?.abort(); };
    // Each locale mounts a separate view; the server retains conversation history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (follow.current && log.current) log.current.scrollTo({ top: log.current.scrollHeight, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [messages, phase]);

  useEffect(() => {
    if (input.current) { input.current.style.height = 'auto'; input.current.style.height = `${Math.min(input.current.scrollHeight, 160)}px`; }
  }, [text]);

  useEffect(() => {
    if (!pending && restoreFocus.current) {
      restoreFocus.current = false;
      const focused = document.activeElement;
      if (focused === document.body || focused === input.current || focused?.classList.contains('chat-send') || focused?.classList.contains('chat-email-action')) input.current?.focus({ preventScroll: true });
    }
  }, [pending]);

  async function send(retry = null, suggestedMessage = null) {
    if (busy.current || !session) return;
    const value = retry?.message ?? suggestedMessage ?? text.trim();
    if (!value || Array.from(value).length > 2000) return;
    const task = retry || { requestId: requestId(), message: value };
    busy.current = true; follow.current = true; restoreFocus.current = true;
    setPhase('sending'); setError(null); setFailed(null);
    if (!retry) { setMessages(items => [...items, { role: 'user', text: value, id: task.requestId }]); if (suggestedMessage === null) setText(''); }
    const controller = new AbortController(); active.current = controller;
    const timer = setTimeout(() => controller.abort(), 35000);
    try {
      const result = await api({ ...task, action: 'message', locale, conversation: session.conversation }, controller.signal);
      if (!mounted.current) return;
      setMessages(result.messages); setAnnouncement(result.reply.text); setPhase('ready');
    } catch (e) {
      if (!mounted.current) return;
      const code = e.name === 'AbortError' ? 'timeout' : e.message;
      setError(code); setFailed(e.retryNew ? { ...task, requestId: requestId() } : task); setPhase('error');
    } finally {
      clearTimeout(timer); busy.current = false;
    }
  }

  async function reset() {
    if (busy.current) return;
    if (!session) { connect(); return; }
    if (messages.length && !window.confirm(c.resetConfirm)) return;
    busy.current = true; setPhase('connecting'); setError(null);
    const controller = new AbortController(); active.current = controller;
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      let data;
      try { data = await api({ action: 'reset', conversation: session.conversation }, controller.signal); }
      catch (e) {
        if (e.message !== 'session_expired') throw e;
        const fresh = await api(null, controller.signal);
        data = await api({ action: 'reset', conversation: fresh.conversation }, controller.signal, fresh.csrf);
      }
      if (!mounted.current) return;
      setSession(data); setMessages([]); setFailed(null); setText(''); setAnnouncement(''); setPhase('ready');
    } catch (e) {
      if (mounted.current) { setError(e.name === 'AbortError' ? 'timeout' : e.message); setPhase('error'); if (e.message === 'session_expired') setSession(null); }
    } finally { clearTimeout(timer); busy.current = false; }
  }

  return <section className="chat-section" aria-labelledby="chat-heading">
    <div className="chat-panel" data-enter>
      <div className="chat-heading"><h2 id="chat-heading">{c.title}</h2><button type="button" className="chat-link motion-inline-link" onClick={reset} disabled={pending}>{c.reset}</button></div>
      <div className="chat-log" ref={log} role="region" aria-label={c.assistant} tabIndex={0} onScroll={() => { const el = log.current; follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100; }}>
        {[{ role: 'assistant', text: c.welcome, id: 'welcome' }, ...messages].map((message, index) => <div className={`chat-row chat-${message.role}`} key={message.id || index}>
          {message.role === 'assistant' && <Avatar />}
          <div className="chat-bubble"><span className="sr-only">{message.role === 'assistant' ? c.assistant : c.you} : </span><p>{message.text}</p>
            {(message.links?.length > 0 || (lastAssistantIndex >= 0 && index === lastAssistantIndex + 1 && !message.draft)) && <div className="chat-actions">
              {message.links?.map(link => <a key={link.href} href={alternatePathFor(locale, link.href)} className="chat-page-link motion-inline-link">{link.label}<span aria-hidden="true"> →</span></a>)}
              {lastAssistantIndex >= 0 && index === lastAssistantIndex + 1 && !message.draft && <button type="button" className="chat-email-action motion-inline-link" disabled={pending || !session || !!error} onClick={() => send(null, c.emailPrompt)}>{c.continueEmail}<span aria-hidden="true"> →</span></button>}
            </div>}
            {message.draft && <EmailDraft draft={message.draft} c={c} />}
          </div>
        </div>)}
        {phase === 'sending' && <div className="chat-row chat-assistant"><Avatar /><div className="chat-bubble chat-thinking" aria-hidden="true"><span /><span /><span /></div></div>}
      </div>
      <div className="sr-only" role="status" aria-live="polite">{phase === 'sending' ? c.waiting : error ? '' : announcement}</div>
      {phase === 'connecting' && <p className="chat-small" role="status">{c.connecting}</p>}
      {error && <div className="chat-error" role="alert"><p>{c.errors[error] || c.errors.network}</p>
        {!['budget', 'conversation_limit', 'session_expired'].includes(error) && <button className="chat-link" type="button" disabled={pending} onClick={() => session && failed ? send(failed) : connect()}>{c.retry}</button>}
      </div>}
      <form className="chat-form" onSubmit={e => { e.preventDefault(); send(); }}>
        <label className="sr-only" htmlFor="chat-message">{c.label}</label>
        <div className="chat-compose"><textarea id="chat-message" ref={input} value={text} rows={1} maxLength={2000} placeholder={c.placeholder} disabled={pending || !session} aria-describedby="chat-hint chat-privacy" onChange={e => setText(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) { e.preventDefault(); send(); }
        }} /><button className="chat-send motion-button" type="submit" aria-label={c.send} disabled={pending || !session || !text.trim()}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 4 17 8-17 8 3-8-3-8Zm3 8h14" /></svg></button></div>
        <p id="chat-hint" className="chat-small chat-hint">{c.hint}</p>
      </form>
    </div>
    <p id="chat-privacy" className="chat-privacy">{c.privacy} <a href={`${pathFor('legalNotice')}#chatbot-data`} className="motion-inline-link">{c.privacyLink}</a></p>
    <p className="chat-direct">{c.direct} <a href={`mailto:${email}`} className="motion-inline-link">{email}</a></p>
  </section>;
}
